/* The ten states every screen must have (design S3.3).
 *
 * They are a component rather than a convention because a convention is what
 * produces a screen where "no results" and "it broke" look identical, and the
 * person cannot tell whether to change the filter or call somebody.
 *
 * Each one says what happened, and what to do next. An empty state with no
 * action is a dead end.
 */
import type { ReactNode } from 'react'

type StateProps = {
  title: string
  detail?: ReactNode
  actions?: ReactNode
  icon?: ReactNode
  kind?: 'plain' | 'error' | 'offline'
}

export function ScreenState({ title, detail, actions, icon, kind = 'plain' }: StateProps) {
  return (
    <div className={`state ${kind === 'plain' ? '' : `state-${kind}`}`} role="status">
      <div className="state-icon" aria-hidden="true">{icon ?? <Dot />}</div>
      <h3>{title}</h3>
      {detail ? <p>{detail}</p> : null}
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  )
}

const Dot = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="10" cy="10" r="7" />
  </svg>
)

/** Loading. Shaped like the thing that is coming, so the page does not jump. */
export function Loading({ rows = 6, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div className="stack" aria-busy="true" aria-live="polite" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel" style={{ height: 'var(--row-h)', opacity: 1 - i * 0.1 }} />
      ))}
      <span className="sr-only">{label}</span>
    </div>
  )
}

/** Nothing here yet, and nobody has tried. Offer the first step. */
export const EmptyFirstTime = ({ what, action }: { what: string; action?: ReactNode }) => (
  <ScreenState title={`No ${what} yet`} detail={`When there are ${what}, they appear here.`} actions={action} />
)

/** Nothing matches the filter. Different from the above, and must look it. */
export const EmptyByFilter = ({ onClear }: { onClear?: () => void }) => (
  <ScreenState
    title="Nothing matches those filters"
    detail="There may still be records here — the filters are hiding them."
    actions={onClear ? <button className="btn" onClick={onClear}>Clear filters</button> : undefined}
  />
)

export const Failed = ({ message, onRetry, requestId }: { message?: string; onRetry?: () => void; requestId?: string }) => (
  <ScreenState
    kind="error"
    title="That did not load"
    detail={<>{message ?? 'Something went wrong at our end.'}{requestId ? <><br /><span className="mono small muted">{requestId}</span></> : null}</>}
    actions={onRetry ? <button className="btn" onClick={onRetry}>Try again</button> : undefined}
  />
)

export const Offline = () => (
  <ScreenState kind="offline" title="You are offline" detail="What you were shown may be out of date. Changes you make are kept and sent when you are back." />
)

/**
 * Refused — and never a dead end. `needs` names the roles that would have
 * granted it, because "no permission" with no route to the fix is a support
 * ticket rather than an answer.
 */
export const NoAccess = ({ what, needs }: { what?: string; needs?: string[] }) => (
  <ScreenState
    title="You do not have access to this"
    detail={
      <>
        {what ? `Your role does not include ${what}. ` : 'Your role does not include this part of the ERP. '}
        {needs?.length ? (
          <>
            Someone with admin rights can grant you{' '}
            {needs.map((role, i) => (
              <span key={role}>
                {i > 0 ? ' or ' : ''}
                <b>{role}</b>
              </span>
            ))}
            .
          </>
        ) : (
          'Ask an administrator for access.'
        )}
      </>
    }
  />
)

/** Not bought, as distinct from not permitted. One is a purchase, the other a role. */
export const NotEntitled = ({ module, onSeeStore }: { module: string; onSeeStore?: () => void }) => (
  <ScreenState
    title={`${module} is not switched on`}
    detail="Your organisation has not added this module yet. Nothing is lost — turning it on brings it straight back."
    actions={onSeeStore ? <button className="btn btn-primary" onClick={onSeeStore}>See what it does</button> : undefined}
  />
)

export const Conflicted = ({ fields, onReload }: { fields: Record<string, unknown>; onReload?: () => void }) => (
  <ScreenState
    kind="error"
    title="Someone else changed this first"
    detail={
      <>
        These are different now:{' '}
        <b>{Object.keys(fields).join(', ')}</b>. Reload to see their version before saving yours.
      </>
    }
    actions={onReload ? <button className="btn" onClick={onReload}>Reload</button> : undefined}
  />
)

export const TooMuchData = ({ count, onNarrow }: { count?: number; onNarrow?: () => void }) => (
  <ScreenState
    title="That is a lot of records"
    detail={`${count ? count.toLocaleString() : 'Too many'} match. Narrow it down and the list will be quicker to read as well as quicker to load.`}
    actions={onNarrow ? <button className="btn" onClick={onNarrow}>Add a filter</button> : undefined}
  />
)

export const Stale = ({ since }: { since?: string }) => (
  <div className="chip chip-warn" title="This was last refreshed a while ago">
    Last updated {since ?? 'a while ago'}
  </div>
)
